import { useEffect, useState, useRef } from "react";
import api, { apiError } from "../api";
import { toast } from "sonner";
import { inr } from "../platforms";
import { UploadCloud, Trash2, Download, Loader2, Plus, Sparkles, ChevronDown, ChevronUp, Landmark } from "lucide-react";

const inputCls = "border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0047AB]/20 w-full";
const labelCls = "text-xs font-semibold uppercase tracking-wider text-slate-600";

function LedgerEditor({ txn, ledgers, onSaved }) {
  const [ledger, setLedger] = useState(txn.ledger || "");
  const [saveRule, setSaveRule] = useState(true);
  const [keyword, setKeyword] = useState("");
  const [busy, setBusy] = useState("");

  const suggest = async () => {
    setBusy("ai");
    try {
      const { data } = await api.post(`/bank/txns/${txn.id}/ai-suggest`);
      setLedger(data.ledger || "");
      setKeyword(data.keyword || "");
      toast.success(`AI suggests: ${data.ledger} (${data.confidence})`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const save = async () => {
    if (!ledger.trim()) return toast.error("Enter a ledger name");
    setBusy("save");
    try {
      const { data } = await api.put(`/bank/txns/${txn.id}`, { ledger, save_rule: saveRule && !!keyword.trim(), keyword });
      toast.success(data.also_applied_to > 0 ? `Saved — rule also mapped ${data.also_applied_to} more transactions` : "Ledger saved");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input data-testid={`txn-ledger-input-${txn.id}`} list="ledger-options" value={ledger} placeholder="Ledger name"
        onChange={(e) => setLedger(e.target.value)} className={`${inputCls} !w-56`} />
      <input data-testid={`txn-keyword-input-${txn.id}`} value={keyword} placeholder="Rule keyword (optional)"
        onChange={(e) => setKeyword(e.target.value.toUpperCase())} className={`${inputCls} !w-44 font-mono text-xs`} />
      <label className="flex items-center gap-1 text-xs text-slate-500 cursor-pointer whitespace-nowrap">
        <input type="checkbox" checked={saveRule} onChange={(e) => setSaveRule(e.target.checked)} /> Save rule
      </label>
      <button data-testid={`txn-ai-btn-${txn.id}`} onClick={suggest} disabled={!!busy} title="AI suggest"
        className="p-1.5 rounded-lg border border-slate-300 text-[#0047AB] hover:border-[#0047AB] transition-colors duration-200 disabled:opacity-50">
        {busy === "ai" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
      </button>
      <button data-testid={`txn-save-btn-${txn.id}`} onClick={save} disabled={!!busy}
        className="bg-[#0047AB] hover:bg-[#003380] text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-colors duration-200 disabled:opacity-50">
        {busy === "save" ? "Saving..." : "Save"}
      </button>
    </div>
  );
}

export default function Bank() {
  const [statements, setStatements] = useState([]);
  const [selected, setSelected] = useState(null);
  const [txns, setTxns] = useState([]);
  const [onlyUnmapped, setOnlyUnmapped] = useState(false);
  const [ledgers, setLedgers] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [showMappings, setShowMappings] = useState(false);
  const [newMap, setNewMap] = useState({ keyword: "", ledger: "", drcr: "" });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadStatements = () => api.get("/bank/statements").then((r) => setStatements(r.data));
  const loadLedgers = () => api.get("/bank/ledgers").then((r) => setLedgers(r.data));
  const loadMappings = () => api.get("/bank/mappings").then((r) => setMappings(r.data));
  const loadTxns = (id, unmappedOnly) =>
    api.get(`/bank/statements/${id}/txns`, { params: { only_unmapped: unmappedOnly } }).then((r) => setTxns(r.data));

  useEffect(() => { loadStatements(); loadLedgers(); loadMappings(); }, []);
  useEffect(() => { if (selected) loadTxns(selected.id, onlyUnmapped); }, [selected, onlyUnmapped]);

  const refreshAll = () => {
    loadStatements();
    loadLedgers();
    if (selected) {
      loadTxns(selected.id, onlyUnmapped);
      api.get("/bank/statements").then((r) => setSelected(r.data.find((s) => s.id === selected.id) || null));
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/bank/upload", fd, { timeout: 120000 });
      toast.success(`Staged ${data.txn_count} transactions (${data.mapped_count} auto-mapped, ${data.unmapped} need attention, ${data.skipped_duplicates} duplicates skipped)`);
      loadStatements();
      setSelected(data);
      setOnlyUnmapped(data.unmapped > 0);
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const exportCsv = async (stmt) => {
    try {
      const res = await api.get(`/bank/statements/${stmt.id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bank_statement_${stmt.period_from}_${stmt.period_to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const addMapping = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/bank/mappings", newMap);
      toast.success(data.applied_to > 0 ? `Rule added — mapped ${data.applied_to} existing transactions` : "Rule added");
      setNewMap({ keyword: "", ledger: "", drcr: "" });
      loadMappings();
      refreshAll();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="bank-page" className="space-y-6">
      <datalist id="ledger-options">{ledgers.map((l) => <option key={l} value={l} />)}</datalist>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Banking</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>Bank Statements</h1>
          <p className="text-sm text-slate-500 mt-1">Upload the bank portal XLSX — transactions are staged, auto-mapped to ledgers, and exportable for your accounting software.</p>
        </div>
        <div className="flex gap-3">
          <input ref={fileRef} type="file" accept=".xlsx,.XLSX" className="hidden" data-testid="bank-file-input"
            onChange={(e) => upload(e.target.files[0])} />
          <button data-testid="bank-upload-btn" disabled={uploading} onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-2 bg-[#0047AB] hover:bg-[#003380] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200 disabled:opacity-60">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
            {uploading ? "Staging..." : "Upload Statement (.xlsx)"}
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg">
        <button data-testid="mappings-toggle-btn" onClick={() => setShowMappings((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-700">
          <span>Ledger Mapping Rules ({mappings.length})</span>
          {showMappings ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {showMappings && (
          <div className="border-t border-slate-100 p-4 space-y-4">
            <form onSubmit={addMapping} className="flex flex-wrap gap-2">
              <input data-testid="new-mapping-keyword" required placeholder="Keyword (e.g. EASEBUZZ)" value={newMap.keyword}
                onChange={(e) => setNewMap({ ...newMap, keyword: e.target.value.toUpperCase() })} className={`${inputCls} !w-48 font-mono text-xs`} />
              <input data-testid="new-mapping-ledger" required list="ledger-options" placeholder="Ledger name" value={newMap.ledger}
                onChange={(e) => setNewMap({ ...newMap, ledger: e.target.value })} className={`${inputCls} !w-64`} />
              <select value={newMap.drcr} onChange={(e) => setNewMap({ ...newMap, drcr: e.target.value })} className={`${inputCls} !w-32`}>
                <option value="">Any Dr/Cr</option><option value="CR">Credit only</option><option value="DR">Debit only</option>
              </select>
              <button data-testid="new-mapping-add-btn" className="inline-flex items-center gap-1 bg-[#0047AB] hover:bg-[#003380] text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors duration-200">
                <Plus size={13} /> Add Rule
              </button>
            </form>
            <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
              {mappings.map((m) => (
                <span key={m.id} className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1 text-xs">
                  <span className="font-mono font-semibold">{m.keyword}</span>
                  {m.drcr && <span className={`px-1 rounded text-[10px] font-bold ${m.drcr === "CR" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{m.drcr}</span>}
                  <span className="text-slate-400">→</span> {m.ledger}
                  <button onClick={async () => { await api.delete(`/bank/mappings/${m.id}`); loadMappings(); }}
                    className="text-slate-400 hover:text-red-600 transition-colors duration-200"><Trash2 size={11} /></button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-[320px,1fr] gap-4 items-start">
        <div className="space-y-2">
          {statements.map((s) => (
            <button key={s.id} data-testid={`statement-card-${s.id}`} onClick={() => setSelected(s)}
              className={`w-full text-left bg-white border rounded-lg p-3 transition-colors duration-200 ${selected?.id === s.id ? "border-[#0047AB] ring-2 ring-[#0047AB]/15" : "border-slate-200 hover:border-slate-400"}`}>
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <Landmark size={14} className="text-[#0047AB] shrink-0" />
                <span className="truncate">{s.period_from} → {s.period_to}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">A/c {s.account_no} · {s.txn_count} txns ·
                <span className={s.mapped_count === s.txn_count ? " text-emerald-600 font-semibold" : " text-amber-600 font-semibold"}>
                  {" "}{s.txn_count - s.mapped_count === 0 ? "fully mapped" : `${s.txn_count - s.mapped_count} unmapped`}
                </span>
              </div>
              <div className="flex gap-2 mt-2">
                <span onClick={(e) => { e.stopPropagation(); exportCsv(s); }} data-testid={`statement-export-${s.id}`}
                  className="inline-flex items-center gap-1 text-xs text-[#0047AB] hover:underline cursor-pointer"><Download size={12} /> Export CSV</span>
                <span onClick={async (e) => { e.stopPropagation(); if (window.confirm("Delete this statement and its transactions?")) { await api.delete(`/bank/statements/${s.id}`); setSelected(null); loadStatements(); } }}
                  data-testid={`statement-delete-${s.id}`}
                  className="inline-flex items-center gap-1 text-xs text-red-500 hover:underline cursor-pointer"><Trash2 size={12} /> Delete</span>
              </div>
            </button>
          ))}
          {statements.length === 0 && <div className="text-sm text-slate-400 bg-white border border-dashed border-slate-300 rounded-lg p-6 text-center">No statements uploaded yet.</div>}
        </div>

        <div className="bg-white border border-slate-200 rounded-lg">
          {!selected ? (
            <div className="p-10 text-center text-sm text-slate-400">Select a statement to preview its transactions.</div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
                <div className="text-sm font-semibold text-slate-800">{selected.filename}</div>
                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                  <input data-testid="unmapped-filter-toggle" type="checkbox" checked={onlyUnmapped} onChange={(e) => setOnlyUnmapped(e.target.checked)} />
                  Show unmapped only
                </label>
              </div>
              <div className="overflow-x-auto max-h-[65vh] overflow-y-auto">
                <table className="w-full text-sm min-w-[900px]">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-[#0A192F] text-white text-left">
                      {["Date", "Narration", "Dr/Cr", "Amount", "Ledger"].map((h) => (
                        <th key={h} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody data-testid="txns-body" className="divide-y divide-slate-100">
                    {txns.map((t) => (
                      <tr key={t.id} className={t.mapped ? "" : "bg-amber-50/60"}>
                        <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">{t.txn_date}</td>
                        <td className="px-3 py-2 text-xs max-w-[280px]">{t.particulars}</td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${t.drcr === "CR" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{t.drcr}</span>
                        </td>
                        <td className="px-3 py-2 text-right font-mono whitespace-nowrap">{inr(t.amount)}</td>
                        <td className="px-3 py-2">
                          {t.mapped ? (
                            <span className="inline-flex items-center gap-2 text-xs">
                              <span className="font-medium text-slate-700">{t.ledger}</span>
                              <button data-testid={`txn-unset-${t.id}`} title="Change ledger"
                                onClick={async () => { await api.put(`/bank/txns/${t.id}`, { ledger: t.ledger }); setTxns(txns.map((x) => x.id === t.id ? { ...x, mapped: false } : x)); }}
                                className="text-slate-400 hover:text-[#0047AB] underline transition-colors duration-200">edit</button>
                            </span>
                          ) : (
                            <LedgerEditor txn={t} ledgers={ledgers} onSaved={refreshAll} />
                          )}
                        </td>
                      </tr>
                    ))}
                    {txns.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">{onlyUnmapped ? "All transactions are mapped 🎉" : "No transactions."}</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
